import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui/SyutSelect';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import notificationService from 'services/notificationService';
import { SyutSearchGrid, type SyutSearchGridColumn, type SyutSearchGridFilter } from 'components/ui/SyutSearchGrid';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

/**
 * ============================================================================
 * SERVICE MASTER MODAL COMPONENTS - REUSABLE
 * ============================================================================
 *
 * This file contains reusable modal components for Service Master:
 * - ServiceMasterFormModal: For creating, editing, and copying services
 * - ServiceMasterViewModal: For viewing service details
 * - ServiceMasterDeleteModal: For delete confirmation
 *
 * These components can be imported and used across different screens
 * (e.g., Service Master Screen, Service Invoice Screen, etc.)
 * ============================================================================
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ServiceMasterRecord {
  id: string;
  code: string;
  serviceName: string;
  description: string;
  category: string;
  entities: string[];
  uom: string;
  effectiveFrom: string;
  effectiveTo: string;
  status: 'Active' | 'Inactive';
  glAccount: string;
  taxDetails: TaxDetail[];
  scope: 'BU' | 'Entity' | 'Enterprise';
  createdAt: string;
  createdBy: string;
}

export interface TaxDetail {
  id: string;
  taxType: string;
  taxCode: string;
  taxRate: string;
  jurisdiction: string;
}

export interface ServiceCategory {
  id: string;
  name: string;
  defaultEntities: string[];
  defaultGLAccount: string;
  defaultTaxes: TaxDetail[];
}

export interface GLAccount {
  id: string;
  code: string;
  name: string;
  category: string;
}

export interface Entity {
  id: string;
  code: string;
  name: string;
  type: 'Entity' | 'Business Unit';
}

export interface SampleMasterFormData {
  code: string;
  serviceName: string;
  description: string;
  category: string;
  entities: string[];
  uom: string;
  effectiveFrom: string;
  effectiveTo: string;
  status: 'Active' | 'Inactive';
  glAccount: string;
  taxDetails: TaxDetail[];
  scope: 'BU' | 'Entity' | 'Enterprise';
}

// ============================================================================
// SEARCHABLE DROPDOWN COMPONENT
// ============================================================================

interface SearchableDropdownProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  label
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="form-group">














      {label && <label className="form-label">{label}</label>}
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <div
          className="form-trigger"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span style={{ color: value ? 'var(--text-primary)' : 'var(--text-muted)' }}>
            {value || placeholder}
          </span>
          <IconSystem.chevronDown size={16} />
        </div>

        {isOpen && (
          <div className="dropdown-panel" style={{ maxHeight: '300px' }}>
            {/* Search Input */}
            <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>
              <div style={{ position: 'relative' }}>
                <IconSystem.search size={16} style={{
                  position: 'absolute',
                  left: '0.625rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }} />
                <input
                  type="text"
                  className="dropdown-search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Type to search..."
                  autoFocus
                />
              </div>
            </div>

            {/* Options List */}
            <div style={{ flex: 1, overflowY: 'auto', maxHeight: '250px' }}>
              {filteredOptions.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  No options found
                </div>
              ) : (
                filteredOptions.map((option, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      onChange(option);
                      setIsOpen(false);
                      setSearchQuery('');
                    }}
                    style={{
                      padding: '0.625rem 1rem',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: 'var(--text-primary)',
                      background: value === option ? '#f8fafc' : 'transparent',
                      fontWeight: value === option ? 600 : 400
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = value === option ? '#f8fafc' : 'transparent';
                    }}
                  >
                    {option}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MULTI-SELECT DROPDOWN COMPONENT
// ============================================================================

interface MultiSelectDropdownProps {
  options: Entity[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  label?: string;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedIds,
  onChange,
  label
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(option =>
      option.name.toLowerCase().includes(query) ||
      option.code.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Reset highlighted index when dropdown opens/closes
    if (!isOpen) {
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredOptions.length]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsListRef.current) {
      const highlightedElement = optionsListRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const toggleSelection = useCallback((id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  }, [selectedIds, onChange]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex(prev => {
          const nextIndex = prev + 1;
          return nextIndex >= filteredOptions.length ? 0 : nextIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex(prev => {
          const nextIndex = prev - 1;
          return nextIndex < 0 ? filteredOptions.length - 1 : nextIndex;
        });
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        e.stopPropagation();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          toggleSelection(filteredOptions[highlightedIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
        setSearchQuery('');
        break;
      default:
        // Allow other keys to propagate for typing
        e.stopPropagation();
        break;
    }
  }, [filteredOptions, highlightedIndex, toggleSelection]);

  const toggleSelectAll = () => {
    const selectableIds = filteredOptions.map(opt => opt.id);
    const allFilteredSelected = selectableIds.every(id => selectedIds.includes(id));

    if (allFilteredSelected) {
      // Deselect all filtered options
      onChange(selectedIds.filter(id => !selectableIds.includes(id)));
    } else {
      // Select all filtered options (add to existing selection)
      onChange([...new Set([...selectedIds, ...selectableIds])]);
    }
  };

  const allFilteredSelected = filteredOptions.length > 0 &&
    filteredOptions.every(opt => selectedIds.includes(opt.id));

  // Get selected options with their details
  const selectedOptions = useMemo(() => {
    return options.filter(opt => selectedIds.includes(opt.id));
  }, [options, selectedIds]);

  // Remove a selected item
  const removeSelection = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedIds.filter(selectedId => selectedId !== id));
  }, [selectedIds, onChange]);

  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <div
          className="form-trigger"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            minHeight: '38px',
            maxHeight: '120px',
            overflowY: 'auto',
            padding: '0.375rem 0.5rem',
            paddingRight: '2rem',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.25rem',
            alignItems: 'flex-start',
            alignContent: 'flex-start',
            position: 'relative'
          }}
        >
          {selectedOptions.length === 0 ? (
            <span style={{ color: '#94a3b8', fontSize: '0.85rem', padding: '0.125rem 0.25rem' }}>
              Select entities...
            </span>
          ) : (
            selectedOptions.map(option => (
              <span
                key={option.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  padding: '0.125rem 0.375rem',
                  background: '#e0ebf7',
                  border: '1px solid #cbd5e1',
                  borderRadius: '0.25rem',
                  fontSize: '0.75rem',
                  color: '#1e3a8a',
                  fontWeight: 500,
                  maxWidth: '150px'
                }}
              >
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {option.name}
                </span>
                <button
                  type="button"
                  onClick={(e) => removeSelection(option.id, e)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '14px',
                    height: '14px',
                    padding: 0,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    borderRadius: '50%',
                    color: '#64748b',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#cbd5e1';
                    e.currentTarget.style.color = '#1e293b';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#64748b';
                  }}
                >
                  <IconSystem.x size={10} />
                </button>
              </span>
            ))
          )}
          <IconSystem.chevronDown
            size={16}
            style={{
              position: 'absolute',
              right: '0.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#64748b',
              flexShrink: 0
            }}
          />
        </div>

        {isOpen && (
          <div className="dropdown-panel" style={{ maxHeight: '350px', zIndex: 10060 }}>
            {/* Search Input */}
            <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>
              <div style={{ position: 'relative' }}>
                <IconSystem.search size={16} style={{
                  position: 'absolute',
                  left: '0.625rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }} />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="dropdown-search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={handleKeyDown}
                  onMouseDown={(e) => e.stopPropagation()}
                  placeholder="Search entities..."
                />
              </div>
            </div>

            {/* Select All */}
            {filteredOptions.length > 0 && (
              <div
                onClick={toggleSelectAll}
                style={{
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid var(--border-color)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  color: 'var(--primary)',
                  background: 'var(--bg-secondary)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--bg-secondary)';
                }}
              >
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  readOnly
                  style={{ width: '16px', height: '16px', accentColor: '#2563eb' }}
                />
                Select All {searchQuery && `(${filteredOptions.length} matching)`}
              </div>
            )}

            {/* Options List */}
            <div ref={optionsListRef} style={{ flex: 1, overflowY: 'auto', maxHeight: '200px' }}>
              {filteredOptions.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  No entities found
                </div>
              ) : (
                filteredOptions.map((option, index) => {
                  const isHighlighted = index === highlightedIndex;
                  return (
                    <div
                      key={option.id}
                      onClick={() => toggleSelection(option.id)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      style={{
                        padding: '0.625rem 1rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.85rem',
                        color: 'var(--text-primary)',
                        background: isHighlighted ? '#f8fafc' : 'transparent'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(option.id)}
                        readOnly
                        style={{ width: '16px', height: '16px', accentColor: '#2563eb' }}
                      />
                      <div>
                        <div style={{ fontWeight: 500 }}>{option.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {option.code} • {option.type}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SIMILAR SERVICE NAME SUGGESTIONS
// ============================================================================

interface SimilarServiceSuggestionsProps {
  serviceName: string;
  existingServices: ServiceMasterRecord[];
  onSelectExisting: (service: ServiceMasterRecord) => void;
}

export const SimilarServiceSuggestions: React.FC<SimilarServiceSuggestionsProps> = ({
  serviceName,
  existingServices,
  onSelectExisting
}) => {
  const similarServices = useMemo(() => {
    if (!serviceName || serviceName.length < 3) return [];

    const query = serviceName.toLowerCase();
    return existingServices
      .filter(service =>
        service.serviceName.toLowerCase().includes(query) &&
        service.serviceName.toLowerCase() !== query
      )
      .slice(0, 5);
  }, [serviceName, existingServices]);

  if (similarServices.length === 0) return null;

  return (
    <div style={{
      marginTop: '0.5rem',
      padding: '0.75rem',
      background: '#fef3c7',
      border: '1px solid #fbbf24',
      borderRadius: '0.5rem'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '0.5rem'
      }}>
        <IconSystem.alert size={16} style={{ color: '#d97706' }} />
        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#92400e' }}>
          Similar services found in Enterprise
        </span>
      </div>
      <div style={{ fontSize: '0.75rem', color: '#78350f', marginBottom: '0.5rem' }}>
        You may map an existing service instead of creating a duplicate:
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {similarServices.map(service => (
          <div
            key={service.id}
            onClick={() => onSelectExisting(service)}
            style={{
              padding: '0.5rem',
              background: 'var(--bg-primary)',
              border: '1px solid #fbbf24',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#fffbeb';
              e.currentTarget.style.borderColor = '#f59e0b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.borderColor = '#fbbf24';
            }}
          >
            <div style={{ fontWeight: 500, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
              {service.serviceName}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.125rem' }}>
              {service.code} • {service.category}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// SERVICE MASTER FORM MODAL
// ============================================================================

const INITIAL_FORM_DATA: SampleMasterFormData = {
  code: '',
  serviceName: '',
  description: '',
  category: '',
  entities: [],
  uom: '',
  effectiveFrom: '',  // set to today when modal opens
  effectiveTo: '',
  status: 'Active',
  glAccount: '',
  taxDetails: [],
  scope: 'Entity',
};

export interface SampleMasterFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  /** The record to populate in edit/copy mode. Null/undefined in create mode. */
  service?: ServiceMasterRecord | null;
  onSave: () => void;
  onClose: () => void;

  // Data sources
  categories: ServiceCategory[];
  entities: Entity[];
  glAccounts: GLAccount[];
  uoms: string[];
  taxServices: TaxDetail[];
  existingServices: ServiceMasterRecord[];

  // Optional callbacks
  onSelectExistingService?: (service: ServiceMasterRecord) => void;

  // Current logged-in entity/company context
  currentEntityId?: string;
  currentEntityName?: string;

  // DEBUG: Track which component opened this modal
  openedBy?: string;

  // Minimize-to-bar support (requires enableModalMinimize config flag)
  tabId?: string;       // Which tab owns this modal (e.g., "sample-master")
  moduleName?: string;  // Shown as badge in the minimized bar (e.g., "Sample Master")
  onReopen?: () => void; // Legacy: kept for backward compat; prefer onRestoreModal
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, serviceCode?: string) => void; // Called by minimized strip to reopen the modal with preserved form state
  createInstanceId?: string; // Unique ID for each Add session — enables multiple minimized Add modals
  copyInstanceId?: string; // Unique ID for each Copy session — enables multiple minimized Copy modals
  minimizeRef?: React.RefObject<(() => void) | null>; // Ref for parent to programmatically minimize
}

export const SampleMasterFormModal: React.FC<SampleMasterFormModalProps> = ({
  isOpen,
  mode,
  service,
  onSave,
  onClose,
  categories,
  entities,
  glAccounts,
  uoms,
  taxServices,
  existingServices,
  onSelectExistingService,
  currentEntityId,
  currentEntityName,
  openedBy,
  tabId,
  moduleName,
  onReopen,
  onRestoreModal,
  createInstanceId,
  copyInstanceId,
  minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  // Internal form state (Pattern B) — modal owns formData
  const [formData, setFormData] = useState<SampleMasterFormData>(INITIAL_FORM_DATA);
  // Always-current refs so callbacks don't go stale on every keystroke
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const serviceRef = useRef(service);
  serviceRef.current = service;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  // Snapshot of the form data at open time — used for dirty tracking
  const initialSnapshotRef = useRef('');
  // When true, the next isOpen=true is a restore — skip form reset to preserve user edits
  const isRestoreRef = useRef(false);
  const [isMappingMode, setIsMappingMode] = useState(false);
  const [selectedExistingService, setSelectedExistingService] = useState<ServiceMasterRecord | null>(null);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Populate form on open. Skip on restore to preserve user edits.
  // Reads mode/service from refs to avoid stale closure issues.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const currentMode = modeRef.current;
    const currentService = serviceRef.current;
    let data: SampleMasterFormData;
    if ((currentMode === 'edit' || currentMode === 'copy') && currentService) {
      data = {
        code: currentMode === 'copy' ? '' : currentService.code,
        serviceName: currentMode === 'copy' ? currentService.serviceName + ' (Copy)' : currentService.serviceName,
        description: currentService.description,
        category: currentService.category,
        entities: currentService.entities,
        uom: currentService.uom,
        effectiveFrom: currentMode === 'copy' ? new Date().toISOString().split('T')[0] : currentService.effectiveFrom,
        effectiveTo: currentMode === 'copy' ? '' : currentService.effectiveTo,
        status: currentMode === 'copy' ? 'Active' : currentService.status as 'Active' | 'Inactive',
        glAccount: currentService.glAccount,
        taxDetails: currentService.taxDetails,
        scope: currentService.scope,
      };
    } else {
      data = { ...INITIAL_FORM_DATA, effectiveFrom: new Date().toISOString().split('T')[0] };
    }
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
    setIsMappingMode(false);
    setSelectedExistingService(null);
  }, [isOpen]);

  // Dirty tracking: compare current formData against the snapshot taken at open time.
  // Guard: empty snapshot (before effect fires) → not dirty.
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setDiscardConfirmOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;
    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentFormData.code || 'new'}`;
    }
    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.code?.trim();
      const name = currentFormData.serviceName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else if (currentMode === 'copy') {
      const code = currentFormData.code?.trim();
      const name = currentFormData.serviceName?.trim();
      const idx = currentCopyInstanceId || '1';
      title = code ? `Copy - ${code} (${idx})` : name ? `Copy - ${name} (${idx})` : `Copy (${idx})`;
    } else {
      title = `Edit - ${currentFormData.code || 'Service'}`;
    }
    // Capture this session's data and snapshot in the closure at minimize time.
    // When a different session opens later it overwrites formData — so we must
    // restore the saved copies here, not rely on whatever state is current.
    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(savedFormData) !== savedSnapshot : false;
    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);              // restore this session's form data
        initialSnapshotRef.current = savedSnapshot; // restore snapshot for correct dirty tracking
        if (onRestoreModal) onRestoreModal(
          currentMode,
          currentMode === 'create' ? currentCreateInstanceId : currentMode === 'copy' ? currentCopyInstanceId : undefined,
          currentMode !== 'create' ? currentFormData.code : undefined,
        );
        else if (onReopen) onReopen();
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, onReopen]);

  // Expose handleMinimize via ref for parent to programmatically minimize
  useEffect(() => {
    if (minimizeRef) {
      minimizeRef.current = handleMinimize;
    }
    return () => {
      if (minimizeRef) {
        minimizeRef.current = null;
      }
    };
  }, [minimizeRef, handleMinimize]);

  const handleConfirmDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  const getFieldError = useCallback((field: string): string | undefined => {
    return fieldErrors[field];
  }, [fieldErrors]);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // All hooks must be called before any conditional returns
  const handleCategoryChange = useCallback((categoryName: string) => {
    clearFieldError('category');
    const category = categories.find(c => c.name === categoryName);
    if (category) {
      setFormData(prev => ({
        ...prev,
        category: categoryName,
        entities: category.defaultEntities,
        glAccount: category.defaultGLAccount,
        taxDetails: category.defaultTaxes,
      }));
    } else {
      setFormData(prev => ({ ...prev, category: categoryName }));
    }
  }, [categories, clearFieldError]);

  const handleSelectExisting = useCallback((svc: ServiceMasterRecord) => {
    if (onSelectExistingService) {
      onSelectExistingService(svc);
    } else {
      setSelectedExistingService(svc);
      setIsMappingMode(true);
      setFormData({
        code: svc.code,
        serviceName: svc.serviceName,
        description: svc.description,
        category: svc.category,
        entities: currentEntityId ? [currentEntityId] : svc.entities,
        uom: svc.uom,
        effectiveFrom: svc.effectiveFrom,
        effectiveTo: svc.effectiveTo,
        status: svc.status,
        glAccount: svc.glAccount,
        taxDetails: svc.taxDetails,
        scope: svc.scope,
      });
    }
  }, [onSelectExistingService, currentEntityId]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!formData.serviceName) {
      errors.serviceName = 'Service Name is required';
    }
    if (!formData.category) {
      errors.category = 'Category is required';
    }
    if (!formData.uom) {
      errors.uom = 'Unit of Measure is required';
    }

    // Duplicate check (only in create mode, not mapping mode)
    if (formData.serviceName && mode === 'create' && !isMappingMode) {
      const exactMatch = existingServices.find(
        s => s.serviceName.toLowerCase() === formData.serviceName.toLowerCase() &&
        s.scope === 'Enterprise'
      );
      if (exactMatch) {
        errors.serviceName = 'A service with this name already exists at Enterprise level. Please select it from the suggestions below or choose a different name.';
      }
    }

    // Single tax at entity level
    if (formData.scope === 'Entity' && formData.taxDetails.length > 1) {
      errors.taxDetails = 'Only one tax can be selected at Entity level';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Focus the first errored field
      const firstErrorField = Object.keys(errors)[0];
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${firstErrorField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA'
            ? el
            : el.querySelector<HTMLElement>('input');
          input?.focus();
        }
      });
      return;
    }

    setFieldErrors({});

    // Stage 1: Show confirmation dialog
    setSaveConfirmOpen(true);
  }, [formData, mode, isMappingMode, existingServices]);

  // Stage 2: User confirmed → lock modal, show loading, call API
  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    setIsSaving(true);

    // Simulate API call — in production, replace with actual async API call
    setTimeout(() => {
      try {
        onSave();
        setIsSaving(false);

        // Stage 3: Show success toast and close modal
        const modeLabels = { create: 'created', edit: 'updated', copy: 'copied' };
        const label = isMappingMode ? 'mapped' : modeLabels[mode];
        notificationService.toast.success(`Service "${formData.serviceName}" has been ${label} successfully.`);

        // Reset mapping mode
        if (isMappingMode) {
          setIsMappingMode(false);
          setSelectedExistingService(null);
        }

        onClose();
      } catch {
        setIsSaving(false);
        notificationService.toast.error('An error occurred while saving. Please try again.');
      }
    }, 800);
  }, [onSave, onClose, mode, isMappingMode, formData.serviceName]);

  // Auto-select entity based on scope
  useEffect(() => {
    if (mode === 'create' && currentEntityId && !isMappingMode) {
      if (formData.scope === 'Entity' || formData.scope === 'BU') {
        if (!formData.entities.includes(currentEntityId)) {
          setFormData(prev => ({ ...prev, entities: [currentEntityId] }));
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.scope, currentEntityId, mode, isMappingMode]);

  // Reset mapping mode when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset mapping mode state when modal is closed
      setIsMappingMode(false);
      setSelectedExistingService(null);
    }
  }, [isOpen]);

  // F2 keyboard shortcut to open product search (only in create mode)
  useEffect(() => {
    if (!isOpen || mode !== 'create' || isMappingMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !productSearchOpen) {
        e.preventDefault();
        setProductSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, mode, isMappingMode, productSearchOpen]);

  // Early return after all hooks have been called
  if (!isOpen) return null;

  // Determine if entity field should be read-only
  const isEntityFieldReadOnly = (formData.scope === 'Entity' || formData.scope === 'BU') && currentEntityId;

  // DEBUG: Log when modal opens (disable in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔵 ServiceMasterFormModal opened', {
      openedBy: openedBy || 'Unknown Component',
      mode,
      formData: formData.serviceName || '(New Service)',
      timestamp: new Date().toISOString()
    });
    console.log('📍 Call stack:', new Error().stack);
  }

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {/* Loading Overlay — position: absolute works because parent has position: fixed */}
        {isSaving && (
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'var(--modal-loading-overlay)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            borderRadius: 'inherit',
          }}>
            <div style={{
              width: '2.5rem',
              height: '2.5rem',
              border: '3px solid var(--border-color)',
              borderTopColor: 'var(--primary)',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }} />
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {isMappingMode ? 'Mapping service...' : mode === 'create' ? 'Creating service...' : 'Saving changes...'}
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">
            {isMappingMode && 'Map Existing Service to Entity'}
            {!isMappingMode && mode === 'create' && 'Add New Master'}
            {!isMappingMode && mode === 'edit' && 'Edit Service'}
            {!isMappingMode && mode === 'copy' && 'Copy Service'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {isMappingMode && selectedExistingService && (
          <div style={{
            margin: '1rem 1.5rem 0',
            padding: '1rem',
            background: '#dcfce7',
            border: '1px solid #16a34a',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <IconSystem.check size={20} style={{ color: '#16a34a', flexShrink: 0 }} />
            <div style={{ fontSize: '0.875rem', color: '#166534' }}>
              <strong>Mapping Mode:</strong> You've selected an existing service from Enterprise level.
              Review the details below and click "Map to Entity" to add it to {currentEntityName || 'your entity'}.
            </div>
          </div>
        )}

        <div className="modal-content">
          <div className="form-group">
            <label className="form-label">Service Code *</label>
            <input
              type="text"
              className="form-input"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
              placeholder="e.g., SVC007"

              readOnly={isMappingMode}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Service Name *</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                data-field="serviceName"
                className={`form-input${getFieldError('serviceName') ? ' form-field-error' : ''}`}
                value={formData.serviceName}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, serviceName: e.target.value }));
                  clearFieldError('serviceName');
                  // Reset mapping mode if user changes the name
                  if (isMappingMode) {
                    setIsMappingMode(false);
                    setSelectedExistingService(null);
                  }
                }}
                placeholder="e.g., Business Consulting"
                style={{ flex: 1 }}
                readOnly={isMappingMode}
              />
              {!isMappingMode && mode === 'create' && (
                <button
                  type="button"
                  onClick={() => setProductSearchOpen(true)}
                  className="btn btn-secondary"
                  title="Search existing products (F2)"
                  style={{
                    padding: '0.5rem 0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <IconSystem.search size={16} />
                  <span style={{ fontSize: '0.8rem' }}>F2</span>
                </button>
              )}
            </div>
            {getFieldError('serviceName') && (
              <div className="form-error-message">{getFieldError('serviceName')}</div>
            )}
            {!isMappingMode && (
              <SimilarServiceSuggestions
                serviceName={formData.serviceName}
                existingServices={existingServices}
                onSelectExisting={handleSelectExisting}
              />
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Detailed description of the service"
              rows={3}

            />
          </div>

          <div className="form-group">
            <label className="form-label">Service Category *</label>
            {isMappingMode ? (
              <input
                type="text"
                className="form-input"
                value={formData.category}
                readOnly
                style={{ background: 'var(--bg-secondary)' }}
              />
            ) : (
              <div data-field="category">
                <SyutSelect
                  options={categories.map(c => ({ value: c.name, label: c.name }))}
                  value={formData.category}
                  onChange={(val) => handleCategoryChange(val as string || '')}
                  placeholder="Select or search category..."
                  isSearchable
                  isClearable
                  size="md"
                  error={getFieldError('category')}
                />
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Associated Entities / Business Units *
              {isEntityFieldReadOnly && (
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: '0.5rem', fontWeight: 400 }}>
                  (Auto-selected for {formData.scope} level)
                </span>
              )}
            </label>
            {isEntityFieldReadOnly || isMappingMode ? (
              <div className="form-input" style={{
                background: 'var(--bg-secondary)'
              }}>
                {currentEntityName || entities.find(e => e.id === currentEntityId)?.name || formData.entities.map(id => entities.find(e => e.id === id)?.name || id).join(', ')}
              </div>
            ) : (
              <SyutSelect
                options={entities.map(e => ({ value: e.id, label: `${e.code} - ${e.name}` }))}
                value={formData.entities}
                onChange={(val) => setFormData(prev => ({ ...prev, entities: (val as string[]) || [] }))}
                placeholder="Select entities..."
                searchPlaceholder="Search entities..."
                isMulti
                variant="checkbox"
                showSelectAll
                selectAllLabel="Select All"
                size="md"
              />
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Unit of Measure (UOM) *</label>
            {isMappingMode ? (
              <input
                type="text"
                className="form-input"
                value={formData.uom}
                readOnly
                style={{ background: 'var(--bg-secondary)' }}
              />
            ) : (
              <div data-field="uom">
                <SyutSelect
                  options={uoms.map(u => ({ value: u, label: u }))}
                  value={formData.uom}
                  onChange={(val) => {
                    setFormData(prev => ({ ...prev, uom: val as string || '' }));
                    clearFieldError('uom');
                  }}
                  placeholder="Select or search UOM..."
                  isSearchable
                  isClearable
                  size="md"
                  error={getFieldError('uom')}
                />
              </div>
            )}
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Effective From</label>
              <input
                type="date"
                className="form-input"
                value={formData.effectiveFrom}
                onChange={(e) => setFormData(prev => ({ ...prev, effectiveFrom: e.target.value }))}
  
              />
            </div>
            <div className="form-group">
              <label className="form-label">Effective To</label>
              <input
                type="date"
                className="form-input"
                value={formData.effectiveTo}
                onChange={(e) => setFormData(prev => ({ ...prev, effectiveTo: e.target.value }))}
  
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Default GL Account</label>
            <SyutSelect
              options={glAccounts.map(gl => ({ value: gl.id, label: `${gl.code} - ${gl.name}` }))}
              value={formData.glAccount}
              onChange={(val) => setFormData(prev => ({ ...prev, glAccount: val as string || '' }))}
              placeholder="Select GL Account..."
              isSearchable
              isClearable
              size="md"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Tax Details</label>
            <div style={{
              padding: '0.75rem',
              background: '#eff6ff',
              border: '1px solid #3b82f6',
              borderRadius: '0.5rem',
              fontSize: '0.85rem',
              color: '#1e40af',
              marginBottom: '0.5rem'
            }}>
              <strong>Note:</strong> Only one tax can be selected at Entity level. Multiple taxes are allowed at Enterprise level.
            </div>
            <div data-field="taxDetails">
              <SyutSelect
                options={taxServices.map(tax => ({
                  value: tax.id,
                  label: `${tax.taxCode} - ${tax.taxRate} (${tax.taxType} - ${tax.jurisdiction})`
                }))}
                value={formData.taxDetails[0]?.id || ''}
                onChange={(val) => {
                  clearFieldError('taxDetails');
                  const tax = taxServices.find(t => t.id === val);
                  if (tax) {
                    setFormData(prev => ({ ...prev, taxDetails: [tax] }));
                  } else {
                    setFormData(prev => ({ ...prev, taxDetails: [] }));
                  }
                }}
                placeholder="Select Tax..."
                isSearchable
                isClearable
                size="md"
                error={getFieldError('taxDetails')}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Scope</label>
            <SyutSelect
              options={[
                { value: 'BU', label: 'BU Only' },
                { value: 'Entity', label: 'Entity Level' },
                { value: 'Enterprise', label: 'Enterprise Level' }
              ]}
              value={formData.scope}
              onChange={(val) => setFormData(prev => ({ ...prev, scope: (val as 'BU' | 'Entity' | 'Enterprise') || 'Entity' }))}
              placeholder="Select Scope..."
              isClearable={false}
              size="md"
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Services created at Entity level are automatically copied to Enterprise level for visibility
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <SyutSelect
              options={[
                { value: 'Active', label: 'Active' },
                { value: 'Inactive', label: 'Inactive' }
              ]}
              value={formData.status}
              onChange={(val) => setFormData(prev => ({ ...prev, status: (val as 'Active' | 'Inactive') || 'Active' }))}
              placeholder="Select Status..."
              isClearable={false}
              size="md"
            />
          </div>
        </div>

        <div className="modal-footer">
          {isMappingMode && (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsMappingMode(false);
                setSelectedExistingService(null);
                setFormData({
                  ...INITIAL_FORM_DATA,
                  effectiveFrom: new Date().toISOString().split('T')[0],
                  entities: currentEntityId ? [currentEntityId] : [],
                });
              }}
            >
              <IconSystem.x size={16} />
              Cancel Mapping
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            <IconSystem.save size={16} />
            {isMappingMode ? 'Map to Entity' : mode === 'create' ? 'Create Service' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Product Search Popup - ERP-style F2 lookup */}
      <ProductSearchPopup<ServiceMasterRecord>
        isOpen={productSearchOpen}
        onClose={() => setProductSearchOpen(false)}
        onSelect={(service) => {
          handleSelectExisting(service);
          setProductSearchOpen(false);
        }}
        title="Search Existing Products/Services"
        data={existingServices}
        columns={[
          { key: 'code', header: 'Code', width: '100px', sortable: true },
          { key: 'serviceName', header: 'Name', width: '200px', sortable: true },
          { key: 'category', header: 'Category', width: '150px', sortable: true },
          { key: 'uom', header: 'UOM', width: '80px', sortable: true },
          {
            key: 'status',
            header: 'Status',
            width: '100px',
            sortable: true,
            render: (value: string) => (
              <span style={{
                padding: '0.125rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 500,
                background: value === 'Active' ? '#dcfce7' : '#fee2e2',
                color: value === 'Active' ? '#166534' : '#991b1b'
              }}>
                {value}
              </span>
            )
          },
          { key: 'scope', header: 'Scope', width: '100px', sortable: true },
        ]}
        filters={[
          {
            key: 'category',
            label: 'Category',
            type: 'select',
            options: categories.map(c => ({ value: c.name, label: c.name }))
          },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' }
            ]
          }
        ]}
        rowKey="id"
        searchPlaceholder="Search products/services..."
        recentlyUsedKey="modal-product-search"
        width="800px"
        height="500px"
      />

      {/* Save Confirmation Dialog */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={isMappingMode ? 'Confirm Mapping' : mode === 'create' ? 'Confirm Create' : mode === 'copy' ? 'Confirm Copy' : 'Confirm Update'}
        message={
          isMappingMode
            ? `Are you sure you want to map "${formData.serviceName}" to ${currentEntityName || 'this entity'}?`
            : mode === 'create'
              ? `Are you sure you want to create the service "${formData.serviceName}"?`
              : mode === 'copy'
                ? `Are you sure you want to copy the service "${formData.serviceName}"?`
                : `Are you sure you want to save changes to "${formData.serviceName}"?`
        }
        confirmLabel={isMappingMode ? 'Map to Entity' : mode === 'create' ? 'Create' : mode === 'copy' ? 'Copy' : 'Save'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Unsaved Changes Confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleConfirmDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

// ============================================================================
// SERVICE MASTER VIEW MODAL
// ============================================================================

export interface ServiceMasterViewModalProps {
  isOpen: boolean;
  service: ServiceMasterRecord | null;
  onClose: () => void;
  onEdit?: (service: ServiceMasterRecord) => void;

  // Optional data sources for displaying related info
  glAccounts?: GLAccount[];
  entities?: Entity[];

  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (serviceCode: string) => void;
}

export const ServiceMasterViewModal: React.FC<ServiceMasterViewModalProps> = ({
  isOpen,
  service,
  onClose,
  onEdit,
  glAccounts,
  entities,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !service) return;
    minimize({
      id: `${tabId}-view-modal-${service.code}`,
      title: `View - ${service.code}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(service.code),
    });
    onClose();
  }, [tabId, moduleName, service, minimize, onClose, onRestoreView]);

  if (!isOpen || !service) return null;

  const glAccount = glAccounts?.find(gl => gl.id === service.glAccount);
  const entitiesDisplay = service.entities.map(eid => {
    const entity = entities?.find(e => e.id === eid);
    return entity?.name || eid;
  }).join(', ');

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header">
          <h2 className="modal-title">Service Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div>
              <label className="form-label">Service Code</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
                {service.code}
              </div>
            </div>
            <div>
              <label className="form-label">Service Name</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
                {service.serviceName}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Description</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
                {service.description}
              </div>
            </div>
            <div>
              <label className="form-label">Category</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
                {service.category}
              </div>
            </div>
            <div>
              <label className="form-label">UOM</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
                {service.uom}
              </div>
            </div>
            <div>
              <label className="form-label">Status</label>
              <span className={`badge badge-${service.status.toLowerCase()}`}>
                {service.status}
              </span>
            </div>
            <div>
              <label className="form-label">Scope</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
                {service.scope}
              </div>
            </div>
            {entities && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Associated Entities</label>
                <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
                  {entitiesDisplay}
                </div>
              </div>
            )}
            {glAccount && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">GL Account</label>
                <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
                  {glAccount.code} - {glAccount.name}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => {
              onClose();
              onEdit(service);
            }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ============================================================================
// SERVICE MASTER DELETE MODAL
// ============================================================================

// ============================================================================
// PRODUCT SEARCH POPUP - ERP-STYLE F2/CTRL+L LOOKUP
// ============================================================================

export interface ProductSearchPopupProps<T = any> {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (record: T) => void;
  title?: string;
  data: T[];
  columns?: SyutSearchGridColumn<T>[];
  filters?: SyutSearchGridFilter[];
  rowKey?: keyof T | string;
  searchPlaceholder?: string;
  width?: string;
  height?: string;
  enableRecentlyUsed?: boolean;
  recentlyUsedKey?: string;
  /** Z-index for the popup (default: 10050 to appear above modals) */
  zIndex?: number;
}

export function ProductSearchPopup<T extends Record<string, any>>({
  isOpen,
  onClose,
  onSelect,
  title = 'Product Search',
  data,
  columns,
  filters,
  rowKey = 'id',
  searchPlaceholder = 'Search products... (F2 to focus)',
  width = '900px',
  height = '600px',
  enableRecentlyUsed = true,
  recentlyUsedKey = 'product-search',
  zIndex = 10050,
}: ProductSearchPopupProps<T>) {
  // Default columns if not provided
  const defaultColumns: SyutSearchGridColumn<T>[] = useMemo(() => [
    { key: 'code', header: 'Code', width: '100px', sortable: true },
    { key: 'productName', header: 'Product Name', width: '200px', sortable: true },
    { key: 'category', header: 'Category', width: '150px', sortable: true },
    { key: 'uom', header: 'UOM', width: '80px', sortable: true },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      sortable: true,
      render: (value: string) => (
        <span style={{
          padding: '0.125rem 0.5rem',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 500,
          background: value === 'Active' ? '#dcfce7' : '#fee2e2',
          color: value === 'Active' ? '#166534' : '#991b1b'
        }}>
          {value}
        </span>
      )
    },
  ], []);

  // Default filters if not provided
  const defaultFilters: SyutSearchGridFilter[] = useMemo(() => [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' }
      ]
    },
  ], []);

  return (
    <SyutSearchGrid<T>
      isOpen={isOpen}
      onClose={onClose}
      onSelect={onSelect}
      title={title}
      columns={columns || defaultColumns}
      data={data}
      rowKey={rowKey as string}
      filters={filters || defaultFilters}
      enableRecentlyUsed={enableRecentlyUsed}
      recentlyUsedKey={recentlyUsedKey}
      searchPlaceholder={searchPlaceholder}
      showRowNumbers
      width={width}
      height={height}
      zIndex={zIndex}
    />
  );
}
